<script setup lang="ts">
import { ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'

interface Props {
  value: string
  label?: string
  copiedLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  label: 'Copy',
  copiedLabel: 'Copied!',
})

const COPIED_FEEDBACK_DURATION_MS = 2000

const copied = ref(false)

async function handleCopy() {
  try {
    await navigator.clipboard.writeText(props.value)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, COPIED_FEEDBACK_DURATION_MS)
  } catch {
    // clipboard unavailable — silent fallback
  }
}
</script>

<template>
  <Box
    is="button"
    flex
    align="center"
    gap="1"
    :class="['copy-button', { 'copy-button--copied': copied }]"
    type="button"
    @click="handleCopy"
  >
    <PiIcon :name="copied ? 'check' : 'copy'" class="copy-button__icon" />
    <Text type="caption" weight="medium" color="default" is="span">
      {{ copied ? copiedLabel : label }}
    </Text>
  </Box>
</template>

<style scoped>
.copy-button {
  height: 28px;
  padding: 0 0.75rem;
  background: var(--p-primary-100);
  border: 1px solid var(--p-primary-100);
  border-radius: 12px;
  color: var(--p-primary-700);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;
}

.copy-button:hover {
  background: var(--p-primary-200);
  border-color: var(--p-primary-200);
}

.copy-button--copied {
  background: var(--p-success-50);
  border-color: var(--p-success-50);
  color: var(--p-success-700);
}

.copy-button__icon {
  font-size: 12px;
}
</style>
